<template>
  <div v-if="deck">
    <div class="no-print">
      <ImportDeck
        v-if="isEditable"
        @input="deck = { ...deck, ...$event }"
        class="mr-1"
      />
      <ExportDeck
        :deck="deck"
        :note="note"
        :name="name"
        :versionID="version_id"
        class="mr-1"
      />
      <ShareDeck :deckId="deck_id" :versionId="version_id" class="mr-1" />
      <UnmatchedImageExporter
        :deck="deck"
        :deckName="name"
        :versionID="version_id"
        class="mr-1"
      />
      <PlayInUnbrewed :versionId="version_id" class="mr-1" />

      <b-form-group
        v-if="isEditable"
        label="Deck name"
        description="This name will be shown in public and private listings"
      >
        <b-form-input
          v-model="name"
          :placeholder="deck.hero.name || 'Please enter a name for your deck'"
          size="lg"
        />
      </b-form-group>
      <h1 v-else>
        {{ name }} <small class="text-muted">by {{ creator }}</small>
        <LikeButton v-model="liked" :deckId="deck_id" v-if="!isOwned" />
      </h1>
      <b-form-group v-if="isEditable" label="Version name">
        <b-form-input
          v-model="versionName"
          placeholder="Version name"
          size="lg"
        />
      </b-form-group>
      <h2 v-else>{{ versionName }}</h2>

      <p v-if="isOwned || !published">
        This version is
        <strong>{{ published ? "published" : "private" }}</strong
        >.
      </p>

      <VersionList
        :familyId="deck_id"
        :versions="versions"
        :versionId="version_id"
      />

      <template v-if="isOwned">
        <b-button variant="primary" @click="updateVersion" class="mr-1">
          Save
        </b-button>
        <b-button
          variant="outline-primary"
          @click="createNewVersion"
          class="mr-1"
        >
          Save as new version
        </b-button>
        <b-button
          variant="outline-danger"
          v-b-modal.publish-modal
          v-if="!published"
          class="mr-1"
        >
          Save and publish
        </b-button>
        <b-modal
          id="publish-modal"
          @ok="publishVersion"
          title="Are you sure you want to publish this deck?"
          okTitle="Publish"
        >
          <p>
            Publishing this deck will make it publicly listed on the main Decks
            page. Once a deck is published, it can no longer be edited. You can,
            however, publish revised versions.
          </p>
        </b-modal>
      </template>
      <div>
        <a href="#tracked-games">Jump to tracked games</a>
      </div>
      <div>
        <a href="#comments">Jump to comments</a>
      </div>
      <div class="mt-4">
        <h3>Tags</h3>
        <TagAdder
          v-if="isOwned"
          v-model="tags"
          :options="tagList"
          @input="updateTags"
        ></TagAdder>
        <div v-else>
          <template v-for="tag in tags">
            <b-badge :key="tag">{{ tag }}</b-badge>
            {{ " " }}
          </template>
        </div>
      </div>
    </div>

    <UnmatchedDeckEditor
      v-if="deck"
      :deck="deck"
      :note.sync="note"
      :bggLink.sync="bggLink"
      :isEditable="isEditable"
      ref="deckEditor"
    />

    <DeckGamesInfo
      id="tracked-games"
      :deckId="version_id"
      class="no-print mb-4"
    />
    <DeckComments id="comments" :deckId="version_id" class="no-print mb-4" />
  </div>
</template>

<script>
import ExportDeck from "@/components/ExportDeck.vue";
import ImportDeck from "@/components/ImportDeck.vue";
import LikeButton from "@/components/LikeButton.vue";
import PlayInUnbrewed from "@/components/PlayInUnbrewed.vue";
import ShareDeck from "@/components/ShareDeck.vue";
import TagAdder from "@/components/TagAdder.vue";
import UnmatchedDeckEditor from "@/components/UnmatchedDeckEditor.vue";
import UnmatchedImageExporter from "@/components/UnmatchedImageExporter.vue";
import VersionList from "@/components/VersionList.vue";
import DeckGamesInfo from "@/components/DeckGamesInfo.vue";
import DeckComments from "@/components/comments/DeckComments.vue";

import api from "@/api";
import { UnmatchedDeck } from "@/Unmatched";

export default {
  name: "EditDeck",
  metaInfo() {
    var title = undefined;
    if (this.name && this.creator) {
      title = this.isEditable
        ? `Editing ${this.name} | Fan Decks | UmDb`
        : `${this.name} by ${this.creator} | Fan Decks | UmDb`;
    }
    return {
      title,
    };
  },
  components: {
    ExportDeck,
    ImportDeck,
    LikeButton,
    PlayInUnbrewed,
    ShareDeck,
    TagAdder,
    UnmatchedDeckEditor,
    UnmatchedImageExporter,
    VersionList,
    DeckGamesInfo,
    DeckComments,
  },
  props: {
    id: String,
    familyId: String,
  },
  data() {
    return {
      deck: null,
      name: "",
      versionName: "",
      published: false,
      creator: "",
      versions: [],
      version_id: null,
      deck_id: null,
      note: "",
      tags: [],
      tagList: [],
      bggLink: "",
      liked: null,
    };
  },
  computed: {
    isOwned() {
      return this.creator === this.$store.getters.currentUser;
    },
    isEditable() {
      return this.isOwned;
    },
  },
  methods: {
    async updateVersion() {
      try {
        await api.updateVersion(this.version_id, {
          deck: this.deck,
          name: this.versionName,
          deckName: this.name,
          note: this.note,
          bgg: this.$refs.deckEditor.bggThreadId(),
        });
        this.$root.$bvToast.toast("Your deck has been saved", {
          title: "Success",
          variant: "success",
        });
      } catch (err) {
        this.$root.$bvToast.toast(err.response.statusText, {
          title: "Error saving",
          variant: "danger",
        });
      }
    },
    async publishVersion() {
      try {
        await api.publishVersion(this.version_id, {
          deck: this.deck,
          name: this.versionName,
          note: this.note,
          bgg: this.$refs.deckEditor.bggThreadId(),
        });
        this.$root.$bvToast.toast("Your deck has been published", {
          title: "Success",
          variant: "success",
        });
        this.published = true;
      } catch (err) {
        this.$root.$bvToast.toast(err.response.statusText, {
          title: "Error publishing",
          variant: "danger",
        });
      }
    },
    async createNewVersion() {
      try {
        const newVersion = await api.createNewVersion(this.version_id, {
          name: this.versionName,
          deck: this.deck,
          note: this.note,
          bgg: this.$refs.deckEditor.bggThreadId(),
        });
        this.$root.$bvToast.toast("New version created", {
          title: "Success",
          variant: "success",
        });
        this.$router.push({
          name: "EditVersion",
          params: { familyId: newVersion.family_id, id: newVersion.version_id },
        });
      } catch (err) {
        this.$root.$bvToast.toast(err.response.statusText, {
          title: "Error creating new version",
          variant: "danger",
        });
      }
    },
    async updateTags(tags) {
      try {
        await api.updateTags(this.version_id, tags);
        this.$root.$bvToast.toast("Tags updated successfully", {
          title: "Tags updated",
          variant: "success",
        });
      } catch {
        this.$root.$bvToast.toast("There was a problem updating tags", {
          title: "Tag error",
          variant: "danger",
        });
      }
    },
    getDeck(id, familyId) {
      api
        .getDeck(id)
        .then((deck) => {
          if (deck.family_id !== familyId) {
            this.$router.replace({
              name: "EditVersion",
              params: { familyId: deck.family_id, id: deck.version_id },
              hash: this.$route.hash,
            });
          }
          this.deck = new UnmatchedDeck(deck.deck_data);
          this.name = deck.name;
          this.versionName = deck.version_name;
          this.published = deck.published;
          this.creator = deck.user;
          this.versions = deck.versions;
          this.version_id = deck.version_id;
          this.deck_id = deck.family_id;
          this.note = deck.note;
          this.tags = deck.tags.map((tag) => tag.name);
          this.bggLink = deck.bgg_link || "";
          this.liked = deck.liked;
          this.$refs?.trackedGames?.refresh?.();
        })
        .catch(() => {
          this.$router.replace("/error");
        });
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.getDeck(to.params.id, to.params.familyId);
    next();
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.getDeck(vm.id, vm.familyId);
    });
  },
  mounted() {
    api.getTags().then((tags) => {
      this.tagList = tags;
    });
  },
};
</script>
